import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  Form,
  redirect,
  ActionFunctionArgs,
  LoaderFunctionArgs,
  useLoaderData,
  useFetcher,
  useNavigate,
  useSubmit,
  Link,
} from "react-router-dom";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import { Input } from "../../components/inputs/Input";
import CustomSelect from "../../components/customSelect";
import { FormLayout } from "../../layout/FormLayout";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import {
  organizationStore,
  useOrganizationStore,
} from "../../store/organization";
import { PaymentProcessor } from "../../types/paymentProcessor";
import { StripeConnectStatusPanel } from "../stripe_connect/StripeConnectStatusPanel";
import { UpdatePaymentProcessorByPk, BulkUnassignProcessor } from "./mutations";
import { PAYMENT_PROCESSOR_CATAGORIES, PaymentProcessorById, GET_RECORDS_USING_PROCESSOR } from "./queries";
import { SecondaryButton, TertiaryButton } from "../../components/Button";
import { usePermissions } from "../auth/api";
import PercentageInput from "../../components/inputs/PercentageInput";
import { userStore } from "../../store/user";
import { Categories } from "../../types/item";
import PriceInput from "../../components/inputs/PriceInput";
import DeactivateProcessorModal, { AffectedRecords } from "./DeactivateProcessorModal";
import { Disclosure } from "@headlessui/react";
import { ChevronUpIcon } from "@heroicons/react/20/solid";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { Badge } from "../../components/Badge";
import moment from "moment/moment";

const client = createApolloClient();
const { getState: getUserStore } = userStore;
const { getState: getOrganizationStore } = organizationStore;

export const paymentProcessorEditAction = async ({
  params,
  request,
}: ActionFunctionArgs) => {
  const user = getUserStore().user;

  const { id } = params;
  const formData = await request.formData();
  const body = Object.fromEntries(formData.entries());
  const priceString = formData.get("application_fee_per_transaction") as string;
  // Blank fee field falls back to the platform default of $0.19 — an explicit
  // "0" typed by ops is respected (parses to 0), only an EMPTY field defaults.
  const parsedPrice = parseFloat(priceString);
  const price = Number.isFinite(parsedPrice) ? parsedPrice : 0.19;
  try {
    const processingRate = (body.processing_rate || "0") as string;
    const processingRatePer = (parseFloat(processingRate) / 100).toFixed(8);
    // MERGE with the row's current config instead of replacing it. The form
    // only carries a handful of fields; a plain replace was wiping everything
    // the Stripe Connect flow stores on the row (stripe_mode, charges/payouts/
    // details flags, stripe_onboarded_at, stripe_link_type, vendor linkage,
    // previous_connected_account_id, terminal configuration id) every time
    // ops saved a fee tweak.
    const { data: existingData } = await client.query({
      query: PaymentProcessorById,
      variables: { id },
      fetchPolicy: "network-only",
    });
    const existingConfig =
      existingData?.payment_processor_config_by_pk?.config || {};
    const config =
        body.processor === "stripe"
            ? {
              ...existingConfig,
              connected_account_id: body.connected_account_id,
              // Empty string → null so "no terminal location" reads as absent
              // everywhere (list badges, Add-location prompts, upsert guard).
              platform_account_location_id: body.platform_account_location_id || null,
              currency: body.currency || null,
              deal_structure: body.deal_structure,
              processing_rate: processingRatePer,
              application_fee_per_transaction: price
            }
            : {
              ...existingConfig,
              secretKey: body.secretKey,
              merchantId: body.merchantId,
              consumerKey: body.consumerKey,
              merchantAccountId: body.merchantAccountId || null,
            };
    await client.mutate({
      mutation: UpdatePaymentProcessorByPk,
      variables: {
        id,
        set: {
          payment_processor: body.payment_processor,
          organization_id: body.organization_id,
          processor: body.processor,
          config,
          processing_rate: processingRatePer,
          is_active: body.is_active === "true",
          last_updated_by: user?.id,
        },
      },
    });

    toast.success("Payment processor updated");

    return redirect("/payment-processors/list");
  } catch {
    toast.error("Unable to update payment processor");
    // Must return a value: react-router treats an undefined action result as
    // an invariant failure and replaces the page with the error boundary,
    // losing the user's form state. Returning null keeps them on the form.
    return null;
  }
};

interface UsageLocation {
  id: number;
  name: string;
  is_active: boolean;
  location_type: string;
  event: { id: number; name: string } | null;
  vendor: { id: number; name: string } | null;
  digital_surcharge_percentage: number;
  dynamic_descriptor: string;
  created_at: string;
  updated_at: string;
}

interface UsageEvent {
  id: number;
  name: string;
  is_active: boolean;
  start_date: string;
  end_date: string;
  created_at: string;
  updated_at: string;
}

interface ProcessorUsageData {
  locations: UsageLocation[];
  orderAheadLocations: UsageLocation[];
  eventsPayments: UsageEvent[];
  eventsTicketing: UsageEvent[];
  eventsInvoicing: UsageEvent[];
}

export const paymentProcessorEditLoader = async ({
  params,
}: LoaderFunctionArgs) => {
  const { id } = params;
  const organizationId = getOrganizationStore().organizationId;
  const { data } = await client.query({
    query: PaymentProcessorById,
    variables: {
      id,
    },
  });
  const { data: paymentTypeData } = await client.query({
    query: PAYMENT_PROCESSOR_CATAGORIES,
    variables: {
      where: organizationId
        ? {
            organization_id: {
              _in: [0, organizationId],
            },
            is_active: {
              _eq: true,
            },
            reference_type: {
              _eq: "payment_processors_processor",
            },
          }
        : {},
    },
  });
  return {
    paymentProcessor: data.payment_processor_config_by_pk,
    paymentType: paymentTypeData.dashboard_reference,
  };
};

async function fetchAffectedRecords(processorId: number): Promise<AffectedRecords> {
  const { data } = await client.query({
    query: GET_RECORDS_USING_PROCESSOR,
    variables: { processorId },
    fetchPolicy: "network-only",
  });
  return {
    locations: data.locations || [],
    orderAheadLocations: data.locations_order_ahead || [],
    eventsPayments: data.events_payments || [],
    eventsTicketing: data.events_ticketing || [],
    eventsInvoicing: data.events_invoicing || [],
  };
}

async function bulkUnassign(processorId: number) {
  await client.mutate({
    mutation: BulkUnassignProcessor,
    variables: { processorId },
  });
}

export const PaymentProcessorEdit = () => {
  const { paymentProcessor, paymentType } = useLoaderData() as {
    paymentProcessor: PaymentProcessor;
    paymentType: Categories[];
  };

  // Lazy-load usage data after render
  const [usage, setUsage] = useState<ProcessorUsageData>({
    locations: [],
    orderAheadLocations: [],
    eventsPayments: [],
    eventsTicketing: [],
    eventsInvoicing: [],
  });

  useEffect(() => {
    client
      .query({
        query: GET_RECORDS_USING_PROCESSOR,
        variables: { processorId: paymentProcessor.id },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        setUsage({
          locations: data.locations || [],
          orderAheadLocations: data.locations_order_ahead || [],
          eventsPayments: data.events_payments || [],
          eventsTicketing: data.events_ticketing || [],
          eventsInvoicing: data.events_invoicing || [],
        });
      });
  }, [paymentProcessor.id]);
  const [organization, setOrganization] = useState(
    paymentProcessor.organization_id
  );
  const navigate = useNavigate();
  const submit = useSubmit();
  const [processor, setProcessor] = useState(paymentProcessor.processor);
  const organizations = useOrganizationStore((state) => state.organizations);
  const { deletePermission } = usePermissions("payment_processors");
  // Dashboard organizers can see this page but can't edit anything. The gate
  // mirrors the editCreatePermission check used elsewhere — if the user
  // doesn't have edit access on payment_processors we render the form in
  // read-only mode (fieldset disabled below, Clone/Save hidden).
  const { editCreatePermission } = usePermissions("payment_processors");
  const readOnly = !editCreatePermission;
  const fetcher = useFetcher();
  const [price, setPrice] = useState<number>(paymentProcessor.config.application_fee_per_transaction || 0);
  const handlePriceChange = (value: number) => {
    setPrice(value);
  };
  const handleClone = () => {
    navigate(`/payment-processors/${paymentProcessor.id}/clone`);
  };

  // Deactivate/Delete modal state
  const [showModal, setShowModal] = useState(false);
  const [affectedRecords, setAffectedRecords] = useState<AffectedRecords | null>(null);
  const [pendingFormData, setPendingFormData] = useState<FormData | null>(null);
  const [modalMode, setModalMode] = useState<"deactivate" | "delete">("deactivate");
  const [isActive, setIsActive] = useState(paymentProcessor.is_active);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    // Only intercept if deactivating (was active, now unchecked)
    if (paymentProcessor.is_active && !isActive) {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      setPendingFormData(formData);
      setModalMode("deactivate");

      try {
        const records = await fetchAffectedRecords(paymentProcessor.id);
        setAffectedRecords(records);
        setShowModal(true);
      } catch {
        toast.error("Failed to check for affected records");
      }
    }
    // Otherwise, let the form submit normally
  };

  const handleDeleteClick = async () => {
    setModalMode("delete");

    try {
      const records = await fetchAffectedRecords(paymentProcessor.id);
      setAffectedRecords(records);
      setShowModal(true);
    } catch {
      toast.error("Failed to check for affected records");
    }
  };

  const handleModalConfirm = async () => {
    try {
      // Bulk unassign all references
      await bulkUnassign(paymentProcessor.id);

      if (modalMode === "deactivate" && pendingFormData) {
        // Submit the form to save as inactive
        submit(pendingFormData, {
          method: "post",
          action: `/payment-processors/${paymentProcessor.id}/edit`,
        });
      } else if (modalMode === "delete") {
        // Submit the delete
        fetcher.submit(
          {},
          {
            method: "delete",
            action: `/payment-processors/${paymentProcessor.id}/delete`,
          }
        );
      }

      setShowModal(false);
      setPendingFormData(null);
      setAffectedRecords(null);
    } catch {
      toast.error("Failed to unassign processor from records");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setPendingFormData(null);
    setAffectedRecords(null);
  };

  // AG Grid setup
  const gridRef = useRef<GridApi>();
  const { editCreatePermission: locationEditPermission } = usePermissions("locations");
  const { editCreatePermission: eventsEditPermission } = usePermissions("events");

  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };

  type LocationCellRenderer = (params: {
    value: any;
    data: UsageLocation;
  }) => JSX.Element | null;

  const createLocationCellRenderer = (
    permission: boolean,
    linkFunc: (params: { data: UsageLocation }) => string
  ): LocationCellRenderer => {
    return (params) => {
      if (params.value === undefined) return null;
      return permission ? (
        <Link to={linkFunc(params)}><Badge>{params.value}</Badge></Link>
      ) : (
        <Badge>{params.value}</Badge>
      );
    };
  };

  const locationColDefs: ColDef<UsageLocation>[] = [
    {
      headerName: "Location Name",
      valueGetter: (p) => p.data?.name,
      cellRenderer: createLocationCellRenderer(
        locationEditPermission,
        (p) => `/locations/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Is Active?",
      valueGetter: (p) => p.data?.is_active,
      valueFormatter: (p) => (p.data?.is_active ? "Yes" : "No"),
    },
    {
      headerName: "Location Type",
      valueGetter: (p) => p.data?.location_type,
    },
    {
      headerName: "Event Name",
      valueGetter: (p) => p.data?.event?.name,
      cellRenderer: createLocationCellRenderer(
        eventsEditPermission,
        (p) => `/events/${p.data?.event?.id}/edit`
      ),
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Service Fee %",
      valueGetter: (p) => p.data?.digital_surcharge_percentage,
      valueFormatter: (params) => {
        const value = params.value ?? 0;
        return value !== 0 ? `${value}%` : "";
      },
    },
    {
      headerName: "Dynamic Descriptor",
      valueGetter: (p) => p.data?.dynamic_descriptor,
    },
    {
      headerName: "Created Date",
      valueGetter: (p) => p.data?.created_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
    },
    {
      headerName: "Updated Date",
      valueGetter: (p) => p.data?.updated_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
    },
  ];

  type EventCellRenderer = (params: {
    value: any;
    data: UsageEvent;
  }) => JSX.Element | null;

  const createEventCellRenderer = (
    permission: boolean,
    linkFunc: (params: { data: UsageEvent }) => string
  ): EventCellRenderer => {
    return (params) => {
      if (params.value === undefined) return null;
      return permission ? (
        <Link to={linkFunc(params)}><Badge>{params.value}</Badge></Link>
      ) : (
        <Badge>{params.value}</Badge>
      );
    };
  };

  const eventColDefs: ColDef<UsageEvent>[] = [
    {
      headerName: "Event Name",
      valueGetter: (p) => p.data?.name,
      cellRenderer: createEventCellRenderer(
        eventsEditPermission,
        (p) => `/events/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Is Active?",
      valueGetter: (p) => p.data?.is_active,
      valueFormatter: (p) => (p.data?.is_active ? "Yes" : "No"),
    },
    {
      headerName: "Start Date",
      valueGetter: (p) => p.data?.start_date,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY") : "";
      },
    },
    {
      headerName: "End Date",
      valueGetter: (p) => p.data?.end_date,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY") : "";
      },
    },
    {
      headerName: "Created Date",
      valueGetter: (p) => p.data?.created_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
    },
    {
      headerName: "Updated Date",
      valueGetter: (p) => p.data?.updated_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
    },
  ];

  const usageSections: { label: string; data: any[]; colDefs: ColDef<any>[] }[] = [
    { label: "Locations", data: usage.locations, colDefs: locationColDefs },
    { label: "Order Ahead Locations", data: usage.orderAheadLocations, colDefs: locationColDefs },
    { label: "Events (Payments)", data: usage.eventsPayments, colDefs: eventColDefs },
    { label: "Events (Ticketing)", data: usage.eventsTicketing, colDefs: eventColDefs },
    { label: "Events (Invoicing)", data: usage.eventsInvoicing, colDefs: eventColDefs },
  ];

  return (
    <>
      {processor === "stripe" && (
        <StripeConnectStatusPanel
          processorId={paymentProcessor.id}
          processorName={paymentProcessor.payment_processor}
          config={paymentProcessor.config as any}
        />
      )}
      <Form
        method="post"
        action={`/payment-processors/${paymentProcessor.id}/edit`}
        onSubmit={(e) => {
          if (readOnly) {
            e.preventDefault();
            return;
          }
          handleFormSubmit(e);
        }}
      >
        {readOnly && (
          <div className="mb-3 flex items-start gap-2 border border-yellow-300 bg-yellow-50 text-yellow-800 rounded px-3 py-2 text-sm">
            <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <strong>View only.</strong> You can see this payment processor's
              details but you can't edit them here. Use the Stripe Connect tab
              to continue onboarding, open the Stripe Express dashboard, or
              add a Terminal location for this account.
            </div>
          </div>
        )}
        <fieldset disabled={readOnly} className={readOnly ? "opacity-90" : undefined}>
          <div className="flex">
            <div className="ml-auto py-2">
              {!readOnly && (
                <TertiaryButton onClick={handleClone}>Clone Payment Processor</TertiaryButton>
              )}
              {deletePermission && !readOnly && (
                <SecondaryButton onClick={handleDeleteClick}>
                  Delete
                </SecondaryButton>
              )}
            </div>
          </div>
        <FormLayout>
          <div className="col-span-4">
            <CustomSelect
              title="Organization"
              name="organization_id"
              defaultValue={organization}
              required
              onChange={setOrganization}
              options={organizations.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          </div>
          <div className="col-span-1">
            <IndeterminateCheckbox
              label="Is Active?"
              name="is_active"
              defaultChecked={paymentProcessor.is_active}
              onChange={((v: boolean) => setIsActive(v)) as any}
            />
          </div>
          <div className="col-span-6">
            <CustomSelect
              title="Processor"
              required
              name="processor"
              defaultValue={paymentProcessor.processor}
              onChange={(value) => setProcessor(value as string)}
              options={[
                ...(paymentType?.map((v) => ({
                  label: v.reference_label,
                  value: v.reference_value,
                })) || []),
              ]}
            />
          </div>
          {processor === "mxmerchant" && (
            <>
              <div className="col-span-6">
                <Input
                  label="Payment Processor Name"
                  name="payment_processor"
                  required
                  defaultValue={paymentProcessor.payment_processor}
                />
              </div>
              <div className="col-span-6">
                <Input
                  label="Consumer Key"
                  name="consumerKey"
                  required
                  defaultValue={paymentProcessor.config.consumerKey}
                />
              </div>
              <div className="col-span-6">
                <Input
                  label="Secret Key"
                  name="secretKey"
                  required
                  defaultValue={paymentProcessor.config.secretKey}
                />
              </div>
              <div className="col-span-6">
                <Input
                  label="Merchant ID"
                  name="merchantId"
                  required
                  defaultValue={paymentProcessor.config.merchantId}
                />
              </div>
              <div className="col-span-6">
                <PercentageInput
                    defaultValue={(
                        (paymentProcessor?.processing_rate || 0) * 100
                    ).toFixed(2)}
                    label="Processing Rate Percentage ( 7.5 = 7.5% )"
                    name="processing_rate"
                />
              </div>
            </>
          )}
          {processor === "braintree" && (
            <>
              <div className="col-span-6">
                <Input
                  label="Payment Processor Name"
                  name="payment_processor"
                  required
                  defaultValue={paymentProcessor.payment_processor}
                />
              </div>
              <div className="col-span-6">
                <Input
                  label="Public Key"
                  name="consumerKey"
                  required
                  defaultValue={paymentProcessor.config.consumerKey}
                />
              </div>
              <div className="col-span-6">
                <Input
                  label="Private Key"
                  name="secretKey"
                  required
                  defaultValue={paymentProcessor.config.secretKey}
                />
              </div>
              <div className="col-span-6">
                <Input
                  label="Merchant ID"
                  name="merchantId"
                  required
                  defaultValue={paymentProcessor.config.merchantId}
                />
              </div>
              <div className="col-span-6">
                <Input
                  label="Merchant Account ID"
                  name="merchantAccountId"
                  required
                  defaultValue={paymentProcessor.config.merchantAccountId}
                />
              </div>
              <div className="col-span-6">
                <PercentageInput
                    defaultValue={(
                        (paymentProcessor?.processing_rate || 0) * 100
                    ).toFixed(2)}
                    label="Processing Rate Percentage ( 7.5 = 7.5% )"
                    name="processing_rate"
                />
              </div>
            </>
          )}
          {processor === "stripe" && (
              <>
                <div className="col-span-6">
                  <Input
                      label="Payment Processor Name"
                      name="payment_processor"
                      required
                      defaultValue={paymentProcessor.payment_processor}
                  />
                </div>
                <div className="col-span-6">
                  <Input
                      label="Connected Account ID"
                      name="connected_account_id"
                      required
                      defaultValue={paymentProcessor.config.connected_account_id}
                  />
                </div>
                <div className="col-span-6">
                  {/* This field holds "where this venue's readers live" — for a
                      venue on the destination flow that is a location on
                      RONIN's platform account, and the POS apps read it to
                      connect to a reader. Clearing or mistyping it here used to
                      null the key while the destination flag survived, after
                      which "Add location" would write a fresh CONNECTED-account
                      location into it and point the venue's devices somewhere
                      their connection token cannot see. Read-only for
                      destination rows; change it via the Stripe Connect panel. */}
                  <Input
                      label={
                        paymentProcessor.config.destination_terminal_location_id
                          ? "Terminal Location ID (Ronin platform account — managed by Stripe Connect)"
                          : "Connected Account Location ID"
                      }
                      name="platform_account_location_id"
                      readOnly={
                        !!paymentProcessor.config.destination_terminal_location_id
                      }
                      defaultValue={paymentProcessor.config.platform_account_location_id}
                  />
                </div>
                <div className="col-span-6">
                  <CustomSelect
                      title="Currency"
                      name="currency"
                      required
                      options={[
                        { value: "usd", label: "USD" },
                        { value: "cad", label: "CAD" },
                      ]}
                      defaultValue={paymentProcessor.config.currency}
                  />
                </div>
                <div className="col-span-6">
                  <CustomSelect
                      title="Deal Structure"
                      name="deal_structure"
                      required
                      options={[
                        { value: "percentOnTop", label: "Percent On Top" },
                        { value: "inclusiveOfCustomerCharge", label: "Inclusive of Customer Charge" },
                      ]}
                      defaultValue={paymentProcessor.config.deal_structure}
                  />
                </div>
                <div className="col-span-6">
                  <PercentageInput
                      defaultValue={(
                          (paymentProcessor.config.processing_rate || 0) * 100
                      ).toFixed(2)}
                      label="Processing Rate Percentage ( 7.5 = 7.5% )"
                      name="processing_rate"
                  />
                </div>
                <div className="col-span-6">
                  <PriceInput
                      label="Fee Per Transaction"
                      name="application_fee_per_transaction"
                      onChange={(value) => handlePriceChange(value ? value : 0)}
                      value={price}
                  />
                </div>
              </>
          )}
        </FormLayout>
        </fieldset>
      </Form>

      {usageSections.map(({ label, data, colDefs }) =>
        data.length > 0 ? (
          <div className=" w-full pt-4 " key={label}>
            <Disclosure>
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex w-full justify-between rounded-lg bg-gray-300 px-4 py-2 text-left text-sm font-medium text-primary-900 hover:bg-gray-400 focus:outline-none focus-visible:ring focus-visible:ring-primary-500/75">
                    <span>{label} ({data.length})</span>
                    <ChevronUpIcon
                      className={`${
                        open ? "rotate-180 transform" : ""
                      } h-5 w-5 text-gray-500`}
                    />
                  </Disclosure.Button>
                  <Disclosure.Panel className=" pb-2 text-sm bg-gray-300 text-gray-500  pt-1">
                    <div style={{ height: "75vh" }} className="bg-gray-300 p-1  ">
                      <div
                        className=" w-full rounded-2xl ag-theme-quartz bg-gray-300 p-4  "
                        style={{ width: "100%", height: "100%" }}
                      >
                        <AgGridReact
                          rowData={data}
                          columnDefs={colDefs}
                          rowHeight={rowHeight}
                          autoSizeStrategy={autoSizeStrategy}
                          pagination={true}
                          gridOptions={gridOptions}
                          paginationPageSize={100}
                          onGridReady={(params) => (gridRef.current = params.api)}
                        />
                      </div>
                    </div>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
        ) : null
      )}

      {affectedRecords && (
        <DeactivateProcessorModal
          isOpen={showModal}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          affectedRecords={affectedRecords}
          processorName={paymentProcessor.payment_processor}
          mode={modalMode}
        />
      )}
    </>
  );
};
